'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Upload, X, Loader2, CheckCircle, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type StoryFormData = {
  displayName: string;
  email: string;
  title: string;
  body: string;
  category: string;
  consent: boolean;
};

const CATEGORIES = [
  { value: '', label: 'Select a category…' },
  { value: 'library', label: 'Library memories' },
  { value: 'wartime', label: 'Wartime memories' },
  { value: 'industrial', label: 'Industrial history' },
  { value: 'family', label: 'Family / Personal memories' },
  { value: 'photographs', label: 'Photographs' },
  { value: 'documents', label: 'Documents' },
];

type PhotoPreview = {
  id: string;
  file: File;
  url: string;
  name: string;
};

export default function StoryFormWrapper() {
  const router = useRouter();
  const { user, userProfile, loading } = useAuth();
  const supabase = createClient();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [charCount, setCharCount] = useState(0);

  const isLoggedIn = !loading && !!user;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StoryFormData>();

  // Pre-fill form with user data
  useEffect(() => {
    if (userProfile?.display_name) setValue('displayName', userProfile.display_name);
    if (user?.email) setValue('email', user.email);
  }, [userProfile, user, setValue]);

  const bodyValue = watch('body', '');
  useEffect(() => {
    setCharCount(bodyValue ? bodyValue.length : 0);
  }, [bodyValue]);

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed per submission');
      return;
    }
    const newPhotos: PhotoPreview[] = files
      .filter((f) => f.size <= 10 * 1024 * 1024)
      .map((file, i) => ({
        id: `photo-${Date.now()}-${i}`,
        file,
        url: URL.createObjectURL(file),
        name: file.name,
      }));
    if (newPhotos.length < files.length) {
      toast.error('Some files were skipped — maximum 10MB per photo');
    }
    setPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = '';
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const onSubmit = async (data: StoryFormData) => {
    if (!user) return;
    setSubmitting(true);

    try {
      // 1. Insert story record
      const { data: story, error: storyError } = await supabase
        .from('stories')
        .insert({
          user_id: user.id,
          title: data.title,
          body_text: data.body,
          category: data.category,
          status: 'pending',
        })
        .select()
        .single();

      if (storyError) throw storyError;

      // 2. Upload photos to storage and insert story_photos records
      if (photos.length > 0 && story) {
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const fileExt = photo.file.name.split('.').pop();
          const filePath = `${user.id}/${story.id}/${Date.now()}-${i}.${fileExt}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('story-photos')
            .upload(filePath, photo.file, { upsert: false });

          if (uploadError) {
            console.log('Photo upload error:', uploadError.message);
            continue;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('story-photos')
            .getPublicUrl(filePath);

          await supabase.from('story_photos').insert({
            story_id: story.id,
            image_url: publicUrl,
            display_order: i,
          });
        }
      }

      setSubmitting(false);
      setSubmitted(true);
      toast.success('Story submitted — thank you! It will appear once reviewed.');
    } catch (error: any) {
      setSubmitting(false);
      toast.error(error?.message || 'Could not submit story. Please try again.');
    }
  };

  if (loading) {
    return (
      <section className="pt-32 pb-24 px-6 min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </section>
    );
  }

  if (!isLoggedIn) {
    return (
      <section className="pt-32 pb-24 px-6 min-h-screen flex items-center">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={24} className="text-primary" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-foreground mb-4">
            Sign in to share your story
          </h1>
          <p className="font-sans text-base text-muted-foreground mb-8 leading-relaxed">
            You need to be signed in to submit a Coed Parc story. Create a free account in under a minute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/sign-up-login-screen" className="btn-primary">
              Sign In or Create Account
            </Link>
            <Link href="/" className="btn-outline">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (submitted) {
    return (
      <section className="pt-32 pb-24 px-6 min-h-screen flex items-center">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={28} className="text-primary" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-foreground mb-4">
            Thank you for your story
          </h1>
          <p className="font-sans text-base text-muted-foreground mb-4 leading-relaxed">
            Your submission has been received and is now awaiting review. Once approved, it will appear on the Community Stories page.
          </p>
          <p className="font-sans text-sm text-muted-foreground mb-10">
            We appreciate you taking the time to share your Coed Parc memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/community-stories" className="btn-primary">
              View Community Stories
            </Link>
            <button
              onClick={() => { setSubmitted(false); setPhotos([]); }}
              className="btn-outline"
            >
              Submit Another Story
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-28 pb-24 px-6">
      <div className="max-w-screen-xl mx-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.25em] text-accent mb-4">
              Community
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground leading-tight mb-5">
              Share Your Story
            </h1>
            <p className="font-sans text-base leading-relaxed text-muted-foreground">
              Perhaps you remember the library? Perhaps your parents worked here? Share your memories, photographs or documents and help build the history of Coed Parc.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-8">
            {/* Personal details */}
            <div className="bg-card border border-border rounded-sm p-8">
              <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-6">
                Your details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Display name <span className="text-red-500">*</span>
                  </label>
                  <p className="font-sans text-xs text-muted-foreground mb-1.5">
                    Shown publicly on your approved story.
                  </p>
                  <input
                    type="text"
                    className={`input-field ${errors.displayName ? 'input-error' : ''}`}
                    {...register('displayName', { required: 'Please enter your display name' })}
                  />
                  {errors.displayName && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.displayName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <p className="font-sans text-xs text-muted-foreground mb-1.5">
                    Not shown publicly. For contact only.
                  </p>
                  <input
                    type="email"
                    className={`input-field ${errors.email ? 'input-error' : ''}`}
                    {...register('email', {
                      required: 'Please enter your email address',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Please enter a valid email' },
                    })}
                  />
                  {errors.email && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.email.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Story content */}
            <div className="bg-card border border-border rounded-sm p-8">
              <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-6">
                Your story
              </h2>
              <div className="flex flex-col gap-5">
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Story title <span className="text-red-500">*</span>
                  </label>
                  <p className="font-sans text-xs text-muted-foreground mb-1.5">
                    A short, descriptive title for your memory or contribution.
                  </p>
                  <input
                    type="text"
                    className={`input-field ${errors.title ? 'input-error' : ''}`}
                    placeholder="e.g. My years working at the library, 1974–1989"
                    {...register('title', {
                      required: 'Please give your story a title',
                      maxLength: { value: 120, message: 'Title must be 120 characters or less' },
                    })}
                  />
                  {errors.title && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.title.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`input-field ${errors.category ? 'input-error' : ''}`}
                    {...register('category', { required: 'Please select a category' })}
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={`cat-${cat.value}`} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.category.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block font-sans text-sm font-medium text-foreground">
                      Your story <span className="text-red-500">*</span>
                    </label>
                    <span className={`font-sans text-xs ${charCount > 2000 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {charCount} / 2000
                    </span>
                  </div>
                  <p className="font-sans text-xs text-muted-foreground mb-1.5">
                    Share your memory, connection to Coed Parc, or description of any documents/photographs you are submitting.
                  </p>
                  <textarea
                    rows={10}
                    className={`input-field resize-y ${errors.body ? 'input-error' : ''}`}
                    placeholder="Tell us your Coed Parc story…"
                    {...register('body', {
                      required: 'Please write your story',
                      maxLength: { value: 2000, message: 'Story must be 2000 characters or less' },
                    })}
                  />
                  {errors.body && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.body.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Photo upload */}
            <div className="bg-card border border-border rounded-sm p-8">
              <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                Photographs (optional)
              </h2>
              <p className="font-sans text-sm text-muted-foreground mb-6">
                You may attach up to 5 photographs. JPG or PNG, maximum 10MB each.
              </p>

              {photos.length < 5 && (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-sm p-8 cursor-pointer hover:border-accent hover:bg-secondary/50 transition-colors mb-5">
                  <Upload size={24} className="text-muted-foreground mb-3" />
                  <p className="font-sans text-sm font-medium text-foreground mb-1">
                    Click to upload photographs
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    JPG, PNG · Up to 10MB each · Max {5 - photos.length} more
                  </p>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    className="hidden"
                    onChange={handlePhotoAdd}
                  />
                </label>
              )}

              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative group aspect-square overflow-hidden rounded-sm border border-border">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={photo.url}
                        alt={`Preview of uploaded photo ${photo.name}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/30 transition-colors" />
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-2 right-2 w-7 h-7 bg-foreground/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        aria-label={`Remove photo ${photo.name}`}
                      >
                        <X size={14} />
                      </button>
                      <p className="absolute bottom-0 left-0 right-0 bg-foreground/60 text-white font-sans text-xs px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {photo.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consent & submit */}
            <div className="bg-card border border-border rounded-sm p-8">
              <div className="flex items-start gap-3 mb-6">
                <input
                  type="checkbox"
                  id="consent"
                  className="w-4 h-4 accent-primary mt-0.5 shrink-0"
                  {...register('consent', { required: 'You must confirm consent before submitting' })}
                />
                <label htmlFor="consent" className="font-sans text-sm text-foreground leading-relaxed cursor-pointer">
                  I confirm I have the right to share this content and give Coed Parc permission to publish it if approved. I understand my story will be reviewed before it appears publicly.
                </label>
              </div>
              {errors.consent && (
                <p className="font-sans text-xs text-red-600 mb-5">{errors.consent.message}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-accent flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minWidth: '200px' }}
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                  ) : (
                    'Submit Your Story'
                  )}
                </button>
                <Link href="/community-stories" className="btn-outline text-center">
                  Cancel
                </Link>
              </div>
              <p className="font-sans text-xs text-muted-foreground mt-4">
                Stories are reviewed by our team before publication. We may contact you if we have questions.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}